import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { CronJobService, CronJobRequest } from '../../services/cron-job';
import { NOTIFICATION_CHANNELS, channelLabel } from '../../services/notification';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

@Component({
  selector: 'app-cron-jobs',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    SelectModule,
    TableModule,
    ButtonModule,
    TooltipModule,
    ToggleSwitchModule,
    CheckboxModule,
    InputTextModule,
    TextareaModule,
  ],
  templateUrl: './cron-jobs.html',
  styleUrls: ['./cron-jobs.css'],
})
export class CronJobs implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(CronJobService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  /** Page heading — pulled from the menu so it always matches the clicked item. */
  readonly pageTitle = inject(PageTitleService).title;

  readonly channels = NOTIFICATION_CHANNELS;
  readonly channelLabel = channelLabel;

  // A few common presets to help users who don't know cron syntax.
  readonly cronPresets = [
    { label: 'Every day 9 AM', value: '0 9 * * *' },
    { label: 'Every hour', value: '0 * * * *' },
    { label: 'Every Monday 8 AM', value: '0 8 * * 1' },
    { label: 'First of month', value: '0 0 1 * *' },
  ];

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  jobs = signal<any[]>([]);

  form: FormGroup = this.fb.group({
    id: [0],
    jobName: ['', Validators.required],
    channel: [1, Validators.required],
    cronExpression: ['', Validators.required],
    recipient: ['', Validators.required],
    subject: [''],
    body: ['', Validators.required],
    isActive: [true],
  });

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) this.load();
  }

  load() {
    this.loading.set(true);
    this.svc.getAll().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.jobs.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  openAdd() {
    this.editing.set(false);
    this.form.reset({
      id: 0,
      jobName: '',
      channel: 1,
      cronExpression: '',
      recipient: '',
      subject: '',
      body: '',
      isActive: true,
    });
    this.showModal.set(true);
  }

  openEdit(job: any) {
    this.editing.set(true);
    this.form.reset({
      id: job.id,
      jobName: job.jobName,
      channel: job.channel ?? 1,
      cronExpression: job.cronExpression,
      recipient: job.recipient,
      subject: job.subject ?? '',
      body: job.body,
      isActive: job.isActive ?? true,
    });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  applyPreset(value: string) {
    this.form.get('cronExpression')?.setValue(value);
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toast.warning('Please fill in all required fields');
      return;
    }
    this.saving.set(true);
    const payload = { ...this.form.value, channel: Number(this.form.value.channel) } as CronJobRequest;

    this.svc.save(payload).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save job');
          return;
        }
        this.toast.success(this.editing() ? 'Job updated' : 'Job created');
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  toggle(job: any) {
    const next = !job.isActive;
    this.svc.toggleActive(job.id, next).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to toggle');
          return;
        }
        job.isActive = next; // optimistic local update
        this.jobs.set([...this.jobs()]);
        this.toast.success(next ? 'Job activated' : 'Job paused');
      },
    });
  }

  async remove(job: any) {
    const ok = await this.toast.confirm(`"${job.jobName}" will be deleted.`, 'Delete job?', 'Delete');
    if (!ok) return;
    this.svc.delete(job.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete');
          return;
        }
        this.toast.success('Job deleted');
        this.load();
      },
    });
  }
}
